import { Card } from "@/components/ui/card";
import { Text } from "@/components/tailwind/text";
import { TrendingDown, TrendingUp, Wallet, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { LedgerEntry } from "@/models/ledger";
import { formatDateString } from "@/lib/utils";

interface LedgerSummaryProps {
    startingBalance: number;
    endingBalance: number;
    monthlyTotal: number;
    totalIncome: number;
    totalExpenses: number;
    orderIncome?: number;
    openingBalanceEntry?: LedgerEntry;
}

export function LedgerSummary({
    startingBalance,
    endingBalance,
    monthlyTotal,
    totalIncome,
    totalExpenses,
    orderIncome = 0,
    openingBalanceEntry
}: LedgerSummaryProps) {
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const getValueColor = (value: number) => {
        if (value > 0) return 'text-green-600 dark:text-green-400';
        if (value < 0) return 'text-red-600 dark:text-red-400';
        return 'text-gray-600 dark:text-gray-400';
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Card className="p-4">
                <div className="flex items-center gap-3">
                    <Wallet className="h-5 w-5 text-gray-500" />
                    <div className="flex-1">
                        <div className="flex items-center gap-1">
                            <Text className="text-sm text-gray-500">
                                Anfangsbestand
                                {openingBalanceEntry && " (Eröffnungsbestand)"}
                            </Text>
                            {openingBalanceEntry && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Eröffnungsbestand vom {formatDateString(openingBalanceEntry.transactionDate)}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        <Text className={`text-lg font-semibold ${getValueColor(startingBalance)}`}>
                            {formatCurrency(startingBalance)}
                        </Text>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <div className="flex items-center gap-3">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    <div className="flex-1">
                        <div className="flex items-center gap-1">
                            <Text className="text-sm text-gray-500">Einnahmen</Text>
                            {orderIncome > 0 && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Davon {formatCurrency(orderIncome)} aus abgerechneten Aufträgen</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        <Text className="text-lg font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(totalIncome)}
                        </Text>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <div className="flex items-center gap-3">
                    <TrendingDown className="h-5 w-5 text-red-500" />
                    <div>
                        <Text className="text-sm text-gray-500">Ausgaben</Text>
                        <Text className="text-lg font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(totalExpenses)}
                        </Text>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <div className="flex items-center gap-3">
                    <Wallet className="h-5 w-5 text-gray-500" />
                    <div>
                        <Text className="text-sm text-gray-500">Monatssumme</Text>
                        <Text className={`text-lg font-semibold ${getValueColor(monthlyTotal)}`}>
                            {formatCurrency(monthlyTotal)}
                        </Text>
                    </div>
                </div>
            </Card>

            <Card className="p-4">
                <div className="flex items-center gap-3">
                    <Wallet className="h-5 w-5 text-blue-500" />
                    <div>
                        <Text className="text-sm text-gray-500">Endbestand</Text>
                        <Text className={`text-lg font-semibold ${getValueColor(endingBalance)}`}>
                            {formatCurrency(endingBalance)}
                        </Text>
                    </div>
                </div>
            </Card>
        </div>
    );
}